import { ArrowLeft, ChevronDown, Loader2, Rocket } from "lucide-react";
import * as React from "react";

import {
  useEstimateMutation,
  usePresetsQuery,
} from "@/entities/projects/queries";
import type { EstimateOut } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/shared/ui/radio-group";

import { EstimatePanel } from "./EstimatePanel";
import { PresetCard } from "./PresetCard";

export interface Step2Value {
  target_phrases: number;
  expansion_depth: number;
  dynamics_top_k: number;
}

export interface Step2DepthProps {
  value: Step2Value;
  onChange: (v: Step2Value) => void;
  onBack: () => void;
  onLaunch: () => void;
  launching: boolean;
  /** Yandex есть И (тип=query ИЛИ openai тоже есть). */
  keysReady: boolean;
  /** Что именно не настроено — для точного сообщения. */
  missingKeys: ("yandex" | "openai")[];
}

type PresetName = "economy" | "standard" | "deep";

export function Step2Depth({
  value,
  onChange,
  onBack,
  onLaunch,
  launching,
  keysReady,
  missingKeys,
}: Step2DepthProps): JSX.Element {
  const presetsQ = usePresetsQuery();
  const estimateM = useEstimateMutation();
  const [estimate, setEstimate] = React.useState<EstimateOut | null>(null);
  const [tuningOpen, setTuningOpen] = React.useState(false);

  // Какой пресет выбран сейчас (если параметры совпадают).
  const selectedPreset: PresetName | null = React.useMemo(() => {
    if (!presetsQ.data) return null;
    for (const name of ["economy", "standard", "deep"] as const) {
      const p = presetsQ.data[name].params;
      if (
        p.target_phrases === value.target_phrases &&
        p.expansion_depth === value.expansion_depth &&
        p.dynamics_top_k === value.dynamics_top_k
      ) {
        return name;
      }
    }
    return null;
  }, [presetsQ.data, value]);

  // Debounce 300мс на estimate.
  React.useEffect(() => {
    const t = setTimeout(() => {
      estimateM.mutate(
        {
          target_phrases: value.target_phrases,
          expansion_depth: value.expansion_depth as 0 | 1 | 2,
          dynamics_top_k: value.dynamics_top_k,
        },
        { onSuccess: setEstimate },
      );
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.target_phrases, value.expansion_depth, value.dynamics_top_k]);

  const pickPreset = (name: PresetName): void => {
    const p = presetsQ.data?.[name]?.params;
    if (!p) return;
    onChange({
      target_phrases: p.target_phrases,
      expansion_depth: p.expansion_depth,
      dynamics_top_k: p.dynamics_top_k,
    });
  };

  return (
    <div className="space-y-6">
      {/* Пресеты */}
      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Готовые пресеты
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {(["economy", "standard", "deep"] as const).map((name) => {
            const item = presetsQ.data?.[name];
            if (!item) {
              return (
                <div
                  key={name}
                  className="h-[150px] animate-soft-pulse rounded-2xl border border-white/10 bg-white/[0.03]"
                />
              );
            }
            return (
              <PresetCard
                key={name}
                name={name}
                item={item}
                selected={selectedPreset === name}
                onSelect={() => pickPreset(name)}
              />
            );
          })}
        </div>
      </div>

      {/* Тонкая настройка */}
      <div>
        <button
          type="button"
          onClick={() => setTuningOpen((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-white"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              tuningOpen && "rotate-180",
            )}
          />
          Тонкая настройка
        </button>
        {tuningOpen && (
          <div className="mt-3 space-y-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Сколько фраз искать</Label>
                <span className="font-mono text-sm text-cyan">
                  {value.target_phrases}
                </span>
              </div>
              <input
                type="range"
                min={50}
                max={2000}
                step={50}
                value={value.target_phrases}
                onChange={(e) =>
                  onChange({ ...value, target_phrases: Number(e.target.value) })
                }
                className="w-full accent-purple"
              />
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>50</span>
                <span>2000</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Глубина расширения сидов</Label>
              <RadioGroup
                value={String(value.expansion_depth)}
                onValueChange={(v) =>
                  onChange({ ...value, expansion_depth: Number(v) })
                }
                className="flex gap-4"
              >
                {[0, 1, 2].map((d) => (
                  <label
                    key={d}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <RadioGroupItem value={String(d)} id={`depth-${d}`} />
                    <span>{d}</span>
                    <span className="text-xs text-muted-foreground">
                      {d === 0 && "только сиды"}
                      {d === 1 && "+10 расширений"}
                      {d === 2 && "+ещё 5"}
                    </span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>Кластеров с динамикой (heat-метрики)</Label>
                <span className="font-mono text-sm text-cyan">
                  {value.dynamics_top_k}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={value.dynamics_top_k}
                onChange={(e) =>
                  onChange({ ...value, dynamics_top_k: Number(e.target.value) })
                }
                className="w-full accent-purple"
              />
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>0 (без)</span>
                <span>100</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Прогноз */}
      <EstimatePanel estimate={estimate} loading={estimateM.isPending} />

      {/* Кнопки */}
      {!keysReady && (
        <div className="rounded-xl border border-amber/30 bg-amber/[0.07] p-4 text-sm">
          {missingKeys.includes("yandex") && missingKeys.includes("openai") ? (
            <>Чтобы запустить сбор, настройте оба API-ключа</>
          ) : missingKeys.includes("yandex") ? (
            <>Чтобы запустить сбор, настройте Yandex API-ключ</>
          ) : (
            <>
              Для типа «ниша»/«сайт» нужен OpenAI-ключ. Либо настройте его,
              либо выберите тип «запрос» — он работает без OpenAI.
            </>
          )}{" "}
          →{" "}
          <a href="/settings/keys" className="text-cyan underline">
            /settings/keys
          </a>
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="border-white/15 bg-white/[0.04]"
          disabled={launching}
        >
          <ArrowLeft className="h-4 w-4" /> Назад
        </Button>
        <Button
          type="button"
          onClick={onLaunch}
          disabled={!keysReady || launching || !estimate}
          className="bg-gradient-primary text-white shadow-glow-purple disabled:opacity-60"
        >
          {launching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Rocket className="h-4 w-4" />
          )}
          Запустить сбор
        </Button>
      </div>
    </div>
  );
}
